import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { loansApi } from '@/services/api';
import { toast } from 'sonner';
import {
  Activity, StickyNote, Loader2, Send, Phone, MapPin, UserCheck, Clock,
} from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { formatDateTime } from '@/utils/dateFormat';
import { StatusBadge } from '@/components/loans';

// What each kind of event looks like in the trail. Keys are the action slugs
// the backend writes; anything unknown falls back to the neutral style.
const EVENT_STYLE = {
  note_added: 'bg-amber-100 text-amber-700 border-amber-200',
  status_changed: 'bg-blue-100 text-blue-700 border-blue-200',
  assigned: 'bg-violet-100 text-violet-700 border-violet-200',
  loan_car_set: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  credit_report_pulled: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  document_uploaded: 'bg-sky-100 text-sky-700 border-sky-200',
  document_removed: 'bg-rose-100 text-rose-700 border-rose-200',
  offer_added: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  letter_shared: 'bg-teal-100 text-teal-700 border-teal-200',
  disbursement_saved: 'bg-green-100 text-green-700 border-green-200',
};

const relative = (iso) => {
  if (!iso) return '';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / 1440)}d ago`;
};

const Initial = ({ name, className = '' }) => (
  <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${className}`}>
    {(name || 'S').charAt(0).toUpperCase()}
  </div>
);

export const LeadActivityDrawer = ({ lead, open, onOpenChange, initialTab = 'notes', onChanged }) => {
  const [tab, setTab] = useState(initialTab);
  const [notes, setNotes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);

  const leadId = lead?.id;

  const load = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const [n, a] = await Promise.all([
        loansApi.getNotes(leadId),
        loansApi.getActivities(leadId),
      ]);
      setNotes(n.data || []);
      setActivities(a.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load this lead\u2019s history');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { if (open) { setTab(initialTab); load(); } }, [open, initialTab, load]);

  const addNote = async () => {
    const text = newNote.trim();
    if (!text || !leadId) return;
    setSaving(true);
    try {
      const res = await loansApi.addNote(leadId, text);
      setNotes((prev) => [res.data, ...prev]);
      setNewNote('');
      toast.success('Note saved', { position: 'bottom-center' });
      const a = await loansApi.getActivities(leadId);
      setActivities(a.data || []);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the note');
    } finally {
      setSaving(false);
    }
  };

  // Managers read this to judge how a rep works the book, so the first thing
  // they should see is when the file was last touched.
  const lastTouch = useMemo(() => activities[0]?.created_at || null, [activities]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[540px] flex flex-col h-full p-0 gap-0" data-testid="loan-activity-drawer">
        <SheetHeader className="px-6 py-4 border-b bg-gradient-to-r from-slate-50 to-white flex-shrink-0 space-y-3">
          <SheetTitle className="flex items-center gap-3 text-left">
            <Initial name={lead?.customer_name} className="bg-blue-600 text-white h-10 w-10 text-sm" />
            <div className="min-w-0">
              <div className="font-semibold text-gray-900 truncate" data-testid="activity-drawer-customer">
                {lead?.customer_name || 'Loan lead'}
              </div>
              <div className="text-sm text-gray-500 flex items-center gap-1 font-normal">
                <Phone className="h-3 w-3" /> {lead?.customer_phone || '—'}
              </div>
            </div>
          </SheetTitle>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {lead?.status && <StatusBadge status={lead.status} />}
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 rounded-full">
              <MapPin className="h-3 w-3" /> {lead?.city_name || 'No city'}
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-violet-50 text-violet-700 rounded-full">
              <UserCheck className="h-3 w-3" /> {lead?.assigned_to_name || 'Unassigned'}
            </span>
            {lastTouch && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-700 rounded-full"
                data-testid="activity-last-touch">
                <Clock className="h-3 w-3" /> last touched {relative(lastTouch)}
              </span>
            )}
          </div>
        </SheetHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid grid-cols-2 mx-6 my-3 flex-shrink-0" style={{ width: 'calc(100% - 48px)' }}>
            <TabsTrigger value="notes" className="text-xs gap-1.5" data-testid="loan-notes-tab">
              <StickyNote className="h-3.5 w-3.5" /> Notes {notes.length > 0 && `(${notes.length})`}
            </TabsTrigger>
            <TabsTrigger value="activity" className="text-xs gap-1.5" data-testid="loan-activity-tab">
              <Activity className="h-3.5 w-3.5" /> Activity {activities.length > 0 && `(${activities.length})`}
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 min-h-0 relative">
            <TabsContent value="notes" className="absolute inset-0 flex flex-col m-0">
              <div className="px-6 pb-3 flex-shrink-0">
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="What happened on this call? Keep it specific — what the customer said, what you promised, what is next."
                  className="w-full min-h-[86px] px-3 py-2 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="loan-new-note-input"
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[11px] text-gray-400">Notes are permanent — they are the record of this file.</span>
                  <Button
                    size="sm"
                    onClick={addNote}
                    disabled={saving || !newNote.trim()}
                    data-testid="loan-add-note-button"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                    Add note
                  </Button>
                </div>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 space-y-3" data-testid="loan-notes-list">
                {loading ? (
                  <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
                ) : notes.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                    <StickyNote className="h-10 w-10 mb-2 text-gray-200" />
                    <p className="text-sm">No notes yet — add the first one after your next call.</p>
                  </div>
                ) : notes.map((n) => (
                  <div key={n.id} className="border rounded-xl p-4 bg-white shadow-sm" data-testid={`loan-note-item-${n.id}`}>
                    <div className="flex items-start gap-3">
                      <Initial name={n.user_name} className="bg-blue-50 text-blue-700" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="font-medium text-sm text-gray-900">{n.user_name}</span>
                          <span className="text-xs text-gray-400 whitespace-nowrap">{formatDateTime(n.created_at)}</span>
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap mt-1">{n.note}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="activity" className="absolute inset-0 overflow-y-auto m-0 px-6 pb-6" data-testid="loan-activity-list">
              {loading ? (
                <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
              ) : activities.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <Activity className="h-10 w-10 mb-2 text-gray-200" />
                  <p className="text-sm">Nothing recorded yet on this lead.</p>
                </div>
              ) : (
                <ol className="relative border-l border-slate-200 ml-3 space-y-4 py-1">
                  {activities.map((a) => (
                    <li key={a.id} className="ml-5" data-testid={`loan-activity-item-${a.id}`}>
                      <span className={`absolute -left-[7px] mt-1.5 h-3.5 w-3.5 rounded-full border-2 border-white ${
                        (EVENT_STYLE[a.action] || 'bg-slate-200').split(' ')[0]
                      }`} />
                      <div className="flex items-baseline justify-between gap-2">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
                          EVENT_STYLE[a.action] || 'bg-slate-100 text-slate-600 border-slate-200'
                        }`}>
                          {(a.action || '').replace(/_/g, ' ')}
                        </span>
                        <span className="text-xs text-gray-400 whitespace-nowrap" title={relative(a.created_at)}>
                          {formatDateTime(a.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-800 mt-1">{a.details}</p>
                      {a.old_value && a.new_value && (                        <p className="text-xs text-gray-500 mt-0.5">
                          {String(a.old_value).replace(/_/g, ' ')} <span className="mx-1">→</span>
                          <span className="font-medium text-gray-800">{String(a.new_value).replace(/_/g, ' ')}</span>
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        by {a.user_name || 'System'}
                        {a.source === 'backfill' && (
                          <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-600/80">
                            from the record
                          </span>
                        )}
                      </p>
                    </li>
                  ))}
                </ol>
              )}
            </TabsContent>
          </div>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
};

export default LeadActivityDrawer;
